/**
 * Contas — ligar e desligar é a decisão mais consequente desta interface.
 *
 * `accounts.is_active` é o interruptor de uma marca inteira: desligada, o
 * worker ignora os destinos dela e o calendário para em silêncio. Por isso a
 * tela separa com clareza os dois botões — conferir não muda nada, ativar muda.
 */
import { Api, type Conta } from "../api.js";
import { el, horario, limpar } from "../dom.js";
import { avisar, type Tela } from "../tela.js";

export function telaContas(): Tela {
  return {
    titulo: "Contas",
    async montar(raiz) {
      const { accounts } = await Api.contas();

      raiz.appendChild(
        el(
          "p",
          { class: "sub" },
          "Conta só publica depois de o verify passar contra a plataforma. " +
            "É a única prova de que a credencial funciona PARA AQUELA conta."
        )
      );

      const lista = el("div", { class: "cartoes" });
      raiz.appendChild(lista);

      const redesenhar = (contas: Conta[]): void => {
        limpar(lista);
        for (const c of contas) lista.appendChild(cartao(c, raiz, redesenhar));
      };
      redesenhar(accounts);
    }
  };
}

function cartao(
  c: Conta,
  raiz: HTMLElement,
  recarregar: (cs: Conta[]) => void
): HTMLElement {
  const estado = el(
    "span",
    { class: `pilula ${c.isActive ? "ok" : "off"}` },
    c.isActive ? "ativa" : "desligada"
  );

  const conferida = c.credential.lastCheckedAt
    ? `conferida ${horario(c.credential.lastCheckedAt)}`
    : "nunca conferida";

  const saida = el("div", { class: "saida", hidden: true });

  const rodar = async (
    botao: HTMLButtonElement,
    dryRun: boolean
  ): Promise<void> => {
    botao.disabled = true;
    const antes = botao.textContent ?? "";
    botao.textContent = "conferindo…";
    saida.hidden = true;
    try {
      const r = await Api.verificarConta(c.id, dryRun);
      limpar(saida);
      saida.className = "saida ok";
      saida.appendChild(
        el(
          "span",
          {},
          dryRun
            ? "Passou. Nada foi alterado — é o que o dryRun faz."
            : `Passou e a conta foi ativada. isActive: ${String(r.isActive)}.`
        )
      );
      saida.hidden = false;
      if (!dryRun) {
        const { accounts } = await Api.contas();
        recarregar(accounts);
        return;
      }
    } catch (err) {
      // O erro do verify é específico e útil — "token revogado",
      // "sem permissão nesta conta". Mostrar a frase da API é o ponto.
      limpar(saida);
      saida.className = "saida erro";
      saida.appendChild(
        el("span", {}, err instanceof Error ? err.message : String(err))
      );
      saida.hidden = false;
    } finally {
      botao.disabled = false;
      botao.textContent = antes;
    }
  };

  const conferir = el(
    "button",
    { class: "secundario" },
    "Conferir (sem efeito)"
  ) as HTMLButtonElement;
  conferir.addEventListener("click", () => void rodar(conferir, true));

  const ativar = el("button", {}, "Conferir e ativar") as HTMLButtonElement;
  ativar.addEventListener("click", () => {
    // Confirmação porque ativar libera o worker para publicar nessa conta
    // assim que houver destino agendado — e a próxima janela pode ser em
    // minutos.
    const ok = confirm(
      `Ativar @${c.handle}?\n\n` +
        `A partir daí o worker passa a publicar nesta conta nos horários agendados.`
    );
    if (ok) void rodar(ativar, false);
  });

  const acoes = el("div", { class: "acoes" }, conferir);
  if (!c.isActive) acoes.appendChild(ativar);

  return el(
    "article",
    { class: "cartao" },
    el("header", {}, el("h3", {}, `@${c.handle}`), estado),
    el(
      "dl",
      {},
      el("dt", {}, "marca"),
      el("dd", {}, c.brand),
      el("dt", {}, "plataforma"),
      el("dd", {}, c.platform),
      el("dt", {}, "id externo"),
      el("dd", { class: "mono" }, c.externalId),
      el("dt", {}, "credencial"),
      el("dd", {}, `${c.credential.label} · ${conferida}`)
    ),
    acoes,
    saida
  );
}
